import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { ModalController } from '@ionic/angular';
import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { isUndefined, isNull } from 'lodash-es';
import { filter } from 'rxjs/operators';

import {
  amountMask,
  cleanUpMaskValue,
  rateAmountMask,
  rateAmountSymbolRegExPattern,
  rateMask,
  ratePostfix
} from '../../../constants/maskito.constants';
import { PrimarySubscriptionPlanAccessAliasEnum } from '../../../enums/monetization';
import * as fromCalculatorActions from '../../../store/actions/calculator.actions';
import * as fromCalculatorSelectors from '../../../store/selectors/calculator.selectors';
import * as fromPropertyProfileSelectors from '../../../store/selectors/property-profile.selectors';
import { SearchableSelectOption } from '../../../typings/searchable-select';
import { MonetizationService } from '../../services/monetization.service';
import { ReportModalComponent } from './report-modal/report-modal.component';

interface PitiFormGroup {
  annualOwnersInsurance: FormControl<string | null>;
  annualPropertyTaxes: FormControl<string | null>;
  buyerName: FormControl<string | null>;
  downPayments: FormControl<string | null>;
  homePrice: FormControl<string | null>;
  interestRate: FormControl<string | null>;
  mortgageTerm: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-piti',
  standalone: false,
  templateUrl: './piti.page.html'
})
export class PITIPageComponent {
  private activatedRoute = inject(ActivatedRoute);
  private modalController = inject(ModalController);
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);

  profileId: string | null;
  defaultBackHref: string | undefined;
  mortgageTermData: SearchableSelectOption[] = [
    { name: '5 years', value: '5' },
    { name: '7 years', value: '7' },
    { name: '10 years', value: '10' },
    { name: '15 years', value: '15' },
    { name: '20 years', value: '20' },
    { name: '25 years', value: '25' },
    { name: '30 years', value: '30' },
    { name: '35 years', value: '35' }
  ];

  form: FormGroup<PitiFormGroup> = new FormGroup<PitiFormGroup>({
    annualOwnersInsurance: new FormControl('1000', Validators.required),
    annualPropertyTaxes: new FormControl(null, Validators.required),
    buyerName: new FormControl(null),
    downPayments: new FormControl(null, Validators.required),
    homePrice: new FormControl(null, Validators.required),
    interestRate: new FormControl(null, Validators.required),
    mortgageTerm: new FormControl(null, Validators.required)
  });

  readonly rateMask: MaskitoOptions = rateMask;
  readonly amountMask: MaskitoOptions = amountMask;
  readonly rateAmountMask: MaskitoOptions = rateAmountMask();
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();

  constructor() {
    this.profileId = this.activatedRoute.snapshot.paramMap.get('id') ?? null;
    if (this.profileId) {
      this.defaultBackHref = ['/property-search', 'profile', this.profileId].join('/');
      this.store
        .pipe(
          select(fromPropertyProfileSelectors.selectPropertyProfile(this.profileId)),
          filter((profile) => !isUndefined(profile))
        )
        .subscribe((profile) => {
          const parsedHomePrice = isNull(profile.SalesPrice)
            ? NaN
            : parseFloat(profile.SalesPrice.replace(/,/, '').replace(/\D*/, ''));
          const parsedTaxAmount = isNull(profile.TaxAmount)
            ? NaN
            : parseFloat(profile.TaxAmount.replace(/,/, '').replace(/\D*/, ''));

          this.form.patchValue({
            annualPropertyTaxes: !isNaN(parsedTaxAmount) ? parsedTaxAmount.toString() : null,
            homePrice: !isNaN(parsedHomePrice) ? parsedHomePrice.toString() : null
          });
        });
    } else {
      this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];
    }
    this.store.pipe(select(fromCalculatorSelectors.selectPitiMortgageTerm)).subscribe((mortgageTerm) => {
      const value = mortgageTerm.toString();
      if (this.form.controls.mortgageTerm.value !== value) {
        this.form.controls.mortgageTerm.setValue(value);
      }
    });
  }

  mortgageTermChanged(event: CustomEvent) {
    this.store.dispatch(
      fromCalculatorActions.updatePITIMortgageTerm({ payload: { mortgageTerm: Number(event.detail.value) } })
    );
  }

  async calculate() {
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      const formValues = this.form.getRawValue();

      this.store.dispatch(
        fromCalculatorActions.updatePITICalculatorData({
          payload: {
            annualOwnersInsurance: cleanUpMaskValue(formValues.annualOwnersInsurance, rateAmountSymbolRegExPattern),
            annualPropertyTaxes: cleanUpMaskValue(formValues.annualPropertyTaxes, rateAmountSymbolRegExPattern),
            buyerName: formValues.buyerName || null,
            downPayments: cleanUpMaskValue(formValues.downPayments, rateAmountSymbolRegExPattern),
            homePrice: cleanUpMaskValue(formValues.homePrice, rateAmountSymbolRegExPattern),
            interestRate: cleanUpMaskValue(formValues.interestRate, ratePostfix)
          }
        })
      );

      const modal = await this.modalController.create({
        component: ReportModalComponent
      });

      await modal.present();
    }
  }
}
